import React, { useState, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Sun, Moon, Eye, EyeOff, User, Trophy, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import bgImage from '../../images/presida_BG.jpg';

const RegisterPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const referralCode = searchParams.get('ref');
  const { signUp } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const validatePassword = (password: string) => {
    const lengthValid = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return {
      lengthValid,
      hasUpperCase,
      hasNumber,
      hasSpecialChar,
      isValid: lengthValid && hasUpperCase && hasNumber && hasSpecialChar,
    };
  };

  const passwordValidation = validatePassword(password);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!email || !password || !name) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    if (!passwordValidation.isValid) {
      setError('A senha não atende aos critérios mínimos de segurança');
      return;
    }

    if (!agreeToTerms) {
      setError('Você precisa concordar com os Termos de Uso e Política de Privacidade');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: signUpError } = await signUp(email, password, name, referralCode || undefined);

      if (signUpError) {
        setError(signUpError);
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setError('Ocorreu um erro ao criar sua conta');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card p-8 w-full max-w-md text-center">
          <Trophy className="w-16 h-16 text-primary-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold mb-4">Verifique seu email</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            Enviamos um link de confirmação para {email}. Por favor, verifique sua caixa de entrada e clique no link para ativar sua conta.
          </p>
          <Link to="/login" className="btn-primary">
            Voltar para Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
  className="min-h-screen flex flex-col items-center justify-center bg-cover bg-center bg-no-repeat"
  style={{ backgroundImage: `url(${bgImage})` }}
>
      <div className="absolute inset-0 bg-black/50" />

      <button onClick={toggleTheme} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10">
        {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      <div className="card p-8 w-full max-w-md relative z-10 bg-white/95 dark:bg-dark-paper/95 backdrop-blur-sm">
        <div className="flex flex-col items-center mb-6">
          <img src="https://presida.club/wp-content/uploads/2025/05/logo_presida_vertical.png" alt="Presida.Club" className="h-24 w-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-center">Crie sua conta no Presida.Club</p>
          {referralCode && <div className="mt-2 text-sm text-primary-600 dark:text-primary-400">Você foi convidado por um amigo! 🎉</div>}
        </div>

        {error && <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4 text-sm">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium mb-1">Nome</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400" />
              </div>
              <input id="name" type="text" className="input pl-10" placeholder="Seu nome" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium mb-1">Email</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input id="email" type="email" className="input pl-10" placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="password" className="block text-sm font-medium mb-1">Senha</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input id="password" type={showPassword ? "text" : "password"} className="input pl-10 pr-10" placeholder="********" value={password} onChange={(e) => setPassword(e.target.value)} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            <div className="mt-2 text-xs space-y-1">
              <p className={`flex items-center gap-1 ${passwordValidation.lengthValid ? 'text-green-600' : 'text-gray-400'}`}>✔ Pelo menos 8 caracteres</p>
              <p className={`flex items-center gap-1 ${passwordValidation.hasUpperCase ? 'text-green-600' : 'text-gray-400'}`}>✔ Letra maiúscula</p>
              <p className={`flex items-center gap-1 ${passwordValidation.hasNumber ? 'text-green-600' : 'text-gray-400'}`}>✔ Pelo menos um número</p>
              <p className={`flex items-center gap-1 ${passwordValidation.hasSpecialChar ? 'text-green-600' : 'text-gray-400'}`}>✔ Pelo menos um caractere especial</p>
            </div>
          </div>

          <div className="mb-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm">
                Concordo com os{' '}
                <button 
                  type="button"
                  onClick={() => setShowTermsModal(true)}
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Termos de Uso
                </button>{' '}
                e{' '}
                <button 
                  type="button"
                  onClick={() => setShowPrivacyModal(true)}
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Política de Privacidade
                </button>
              </span>
            </label>
          </div>

          <button type="submit" className="btn-primary w-full" disabled={loading}>{loading ? 'Criando conta...' : 'Criar conta'}</button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Já tem uma conta?{' '}
            <Link to="/login" className="text-primary-600 dark:text-primary-400 font-medium hover:underline">
              Faça login
            </Link>
          </p>
        </div>
      </div>

      {/* Terms of Use Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-dark-paper rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b dark:border-dark-border">
              <h2 className="text-xl font-bold">Termos de Uso</h2>
              <button
                onClick={() => setShowTermsModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-dark-light rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="prose dark:prose-invert max-w-none">
                <h3>1. Aceitação dos Termos</h3>
                <p>
                  Ao acessar e usar o Presida.Club, você concorda em cumprir e estar vinculado aos seguintes termos e condições.
                  Se você não concordar com qualquer parte destes termos, não poderá acessar o serviço.
                </p>

                <h3>2. Uso do Serviço</h3>
                <p>
                  O Presida.Club é uma plataforma de gestão para times de futebol amador. Você concorda em:
                </p>
                <ul>
                  <li>Fornecer informações precisas e atualizadas</li>
                  <li>Manter a segurança de sua conta</li>
                  <li>Não usar o serviço para fins ilegais</li>
                  <li>Respeitar os direitos de outros usuários</li>
                </ul>

                <h3>3. Contas de Usuário</h3>
                <p>
                  Para usar o Presida.Club, você precisa criar uma conta. Você é responsável por:
                </p>
                <ul>
                  <li>Manter a confidencialidade de sua senha</li>
                  <li>Todas as atividades que ocorrem em sua conta</li>
                  <li>Notificar imediatamente sobre qualquer uso não autorizado</li>
                </ul>

                <h3>4. Conteúdo do Usuário</h3>
                <p>
                  Você mantém todos os direitos sobre o conteúdo que envia ao Presida.Club, mas concede uma licença não exclusiva
                  para usar, modificar e exibir o conteúdo para fornecer o serviço.
                </p>

                <h3>5. Limitação de Responsabilidade</h3>
                <p>
                  O Presida.Club é fornecido "como está" e não oferece garantias de qualquer tipo. Não somos responsáveis por:
                </p>
                <ul>
                  <li>Perda de dados</li>
                  <li>Danos indiretos</li>
                  <li>Interrupções do serviço</li>
                </ul>

                <h3>6. Modificações</h3>
                <p>
                  Reservamo-nos o direito de modificar ou descontinuar o serviço a qualquer momento, com ou sem aviso prévio.
                  Também podemos atualizar estes termos periodicamente.
                </p>

                <h3>7. Cancelamento</h3>
                <p>
                  Você pode cancelar sua conta a qualquer momento. Reservamo-nos o direito de suspender ou encerrar contas que violem estes termos.
                </p>

                <h3>8. Lei Aplicável</h3>
                <p>
                  Estes termos são regidos pelas leis do Brasil, sem considerar conflitos de disposições legais.
                </p>

                <h3>9. Contato</h3>
                <p>
                  Para questões sobre estes termos, entre em contato conosco em: contato@presida.club
                </p>
              </div>
            </div>
            <div className="p-6 border-t dark:border-dark-border">
              <button
                onClick={() => setShowTermsModal(false)}
                className="btn-primary w-full"
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-dark-paper rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b dark:border-dark-border">
              <h2 className="text-xl font-bold">Política de Privacidade</h2>
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-dark-light rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="prose dark:prose-invert max-w-none">
                <h3>1. Dados Coletados</h3>
                <p>
                  Coletamos os seguintes tipos de informações:
                </p>
                <ul>
                  <li>Informações de cadastro (nome, email)</li>
                  <li>Dados de jogadores e times</li>
                  <li>Informações financeiras do time</li>
                  <li>Dados de uso do serviço</li>
                </ul>

                <h3>2. Uso dos Dados</h3>
                <p>
                  Utilizamos seus dados para:
                </p>
                <ul>
                  <li>Fornecer e melhorar nossos serviços</li>
                  <li>Processar pagamentos e transações</li>
                  <li>Enviar notificações importantes</li>
                  <li>Personalizar sua experiência</li>
                </ul>

                <h3>3. Base Legal para Processamento (LGPD)</h3>
                <p>
                  Processamos seus dados com base em:
                </p>
                <ul>
                  <li>Execução de contrato</li>
                  <li>Consentimento explícito</li>
                  <li>Interesses legítimos</li>
                  <li>Obrigações legais</li>
                </ul>

                <h3>4. Seus Direitos (LGPD)</h3>
                <p>
                  Você tem os seguintes direitos:
                </p>
                <ul>
                  <li>Acesso aos seus dados</li>
                  <li>Retificação de dados incorretos</li>
                  <li>Apagamento de dados ("direito ao esquecimento")</li>
                  <li>Portabilidade dos dados</li>
                  <li>Restrição de processamento</li>
                  <li>Oposição ao processamento</li>
                </ul>

                <h3>5. Compartilhamento de Dados</h3>
                <p>
                  Compartilhamos dados apenas com:
                </p>
                <ul>
                  <li>Provedores de serviço necessários</li>
                  <li>Autoridades quando legalmente exigido</li>
                </ul>

                <h3>6. Segurança dos Dados</h3>
                <p>
                  Implementamos medidas técnicas e organizacionais para proteger seus dados:
                </p>
                <ul>
                  <li>Criptografia em trânsito e em repouso</li>
                  <li>Controles de acesso rigorosos</li>
                  <li>Monitoramento de segurança</li>
                </ul>

                <h3>7. Retenção de Dados</h3>
                <p>
                  Mantemos seus dados apenas pelo tempo necessário para:
                </p>
                <ul>
                  <li>Fornecer nossos serviços</li>
                  <li>Cumprir obrigações legais</li>
                  <li>Resolver disputas</li>
                </ul>

                <h3>8. Cookies e Tecnologias Similares</h3>
                <p>
                  Usamos cookies para:
                </p>
                <ul>
                  <li>Manter sua sessão</li>
                  <li>Lembrar preferências</li>
                  <li>Melhorar a performance</li>
                </ul>

                <h3>9. Transferências Internacionais</h3>
                <p>
                  Quando transferimos dados para fora do Brasil, garantimos proteções adequadas através de:
                </p>
                <ul>
                  <li>Cláusulas contratuais padrão</li>
                  <li>Decisões de adequação</li>
                  <li>Outras salvaguardas legais</li>
                </ul>

                <h3>10. Contato</h3>
                <p>
                  Para exercer seus direitos ou tirar dúvidas sobre privacidade, contate:
                  <br />
                  Email: privacidade@presida.club
                </p>
              </div>
            </div>
            <div className="p-6 border-t dark:border-dark-border">
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="btn-primary w-full"
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;